import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Copy, ArrowRight, Sparkles, Mail, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function Success() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const license = params.get("license");
  const orderId = params.get("order");
  const [copied, setCopied] = useState(false);
  const [resending, setResending] = useState(false);
  const [resentTo, setResentTo] = useState(null);

  const copy = () => {
    if (!license) return;
    navigator.clipboard.writeText(license);
    setCopied(true);
    toast.success("License key copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const resend = async () => {
    if (!orderId) {
      toast.error("Missing order reference — please contact support if your email didn't arrive.");
      return;
    }
    setResending(true);
    try {
      const r = await api.post(`/orders/${orderId}/resend-email`);
      setResentTo(r.data.email);
      toast.success(`License email re-sent to ${r.data.email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend email — please contact support.");
    } finally {
      setResending(false);
    }
  };

  return (
    <section className="max-w-3xl mx-auto px-6 lg:px-8 py-28" data-testid="success-page">
      <div className="card-tile metal-border p-12">
        <div className="inline-flex items-center justify-center w-14 h-14 bg-[#F4A22A] text-black mb-6">
          <CheckCircle2 className="w-7 h-7" />
        </div>

        <div className="text-xs tracking-[0.25em] uppercase text-neutral-400">Thank you</div>
        <h1 className="font-display font-black tracking-tighter text-4xl md:text-5xl text-white mt-2" data-testid="success-headline">
          Payment received!
        </h1>
        <p className="text-neutral-300 mt-4 text-lg leading-relaxed">
          <span className="text-white">Check your email</span> — your license key and download link arrive within
          <span className="text-[#F4A22A]"> 60 seconds</span>.
        </p>

        {license && (
          <div className="mt-10">
            <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Your license key</div>
            <div className="flex items-stretch border border-white/15">
              <code className="flex-1 font-mono text-white text-lg px-5 py-4 bg-black tracking-wider break-all" data-testid="success-license-key">{license}</code>
              <button onClick={copy} className="px-4 bg-white text-black hover:bg-neutral-200 transition-colors" data-testid="success-copy-button" aria-label="Copy license key">
                <Copy className="w-4 h-4" />
              </button>
            </div>
            {copied && <div className="text-xs text-[#F4A22A] mt-2">Copied to clipboard</div>}
          </div>
        )}

        {/* Email-not-received fallback */}
        <div className="mt-10 border border-white/10 p-6" data-testid="success-resend-block">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-[#1E5BA8] mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="text-white text-sm font-bold">Didn't receive the email?</div>
              <p className="text-neutral-400 text-xs mt-1 leading-relaxed">
                First, check your <span className="text-neutral-200">spam / junk folder</span>. If it's not there, click below to resend.
              </p>
              <button
                onClick={resend}
                disabled={resending || !orderId}
                data-testid="success-resend-button"
                className="mt-4 inline-flex items-center gap-2 border border-white/25 hover:bg-white/5 text-white px-5 py-3 text-xs font-bold uppercase tracking-wider disabled:opacity-60"
              >
                {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Resend license email
              </button>
              {resentTo && (
                <div className="text-[11px] text-[#F4A22A] mt-3" data-testid="success-resent-confirmation">
                  Email re-sent to {resentTo}. Allow up to a minute for delivery.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap gap-4">
          <Link
            to={token ? `/download?token=${token}` : "/download"}
            className="btn-primary px-7 py-4 text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2"
            data-testid="success-download-cta"
          >
            Go to Downloads <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/" className="border border-white/25 hover:bg-white/5 text-white px-7 py-4 text-sm font-bold uppercase tracking-wider transition-colors">
            Back home
          </Link>
        </div>

        <div className="mt-10 flex items-center gap-2 text-[11px] text-neutral-500">
          <Sparkles className="w-3 h-3 text-[#F4A22A]" /> Powered by AI · One license, one PC
        </div>
      </div>
    </section>
  );
}
